import { isEmpty, truncate } from "lodash-es";
import type { UnlayerOptions } from "react-email-editor";
import { useFlags } from "../../../server/optimizely";
import { useSettings } from "../../../utils/hooks/use-settings";
import { notEmpty } from "../../../utils/not-empty";
import { useAccountId } from "../../generic/hooks/use-account-id";
import type { UnlayerMergeTags } from "../../merge-tag/util";
import { makeSurveyUrl } from "../../message/editor/components/utils/survey-url";
import { useUnlayerAccount } from "./use-unlayer-account";
import { useUnlayerSurveys } from "./use-unlayer-surveys";

export function useUnlayerEditorOptions(
  mergeTags: UnlayerMergeTags,
): { loading: true; data: null } | { loading: false; data: UnlayerOptions } {
  const { settings } = useSettings();
  const accountId = useAccountId();
  const unlayerAccount = useUnlayerAccount();

  const {
    flags: { ret_3962_unlayer_table_tool },
    flagsLoaded,
  } = useFlags("ret_3962_unlayer_table_tool");
  const { data: surveys, loading: surveysLoading } = useUnlayerSurveys();

  if (!unlayerAccount || surveysLoading || !accountId || !flagsLoaded) {
    return { loading: true, data: null };
  }

  return {
    loading: false,
    data: {
      id: "editor-email",
      version: !ret_3962_unlayer_table_tool ? "1.309.4" : undefined,
      appearance: { theme: settings.theme },
      projectId: unlayerAccount.projectId as unknown as number,
      user: {
        id: unlayerAccount.userId,
        signature: unlayerAccount.signature,
      },
      features: {
        userUploads: { enabled: true, search: true },
        textEditor: { tables: !ret_3962_unlayer_table_tool },
        preview: false,
      },
      tools: {
        social: {
          enabled: true,
        },
        table: {
          enabled: ret_3962_unlayer_table_tool,
        },
      },
      mergeTags,
      mergeTagsConfig: { sort: false },
      displayMode: "email",
      linkTypes: surveys ? [makeSurveyLinkType(accountId, surveys)].filter(notEmpty) : undefined,
      translations: {
        "en-US": {
          "tools.tabs.uploads": "Library",
        },
      },
      tabs: {
        userUploads: {
          icon: "fa-folder-image",
        },
      },
      // Hides the delete & upload buttons in the custom file library
      customCSS: [
        `.gallery .item-context-menu-button,
            .blockbuilder-uploads-panel header,
            .blockbuilder-uploads-panel .blockbuilder-dropzone-container
               { display: none !important; }`,
        `div.react-select__option { height: 70px !important; white-space: normal !important; word-break: break-word !important; }`,
      ],
    },
  };
}

function makeSurveyLinkType(
  accountId: string,
  surveys: { id: string; title: string }[],
): NonNullable<UnlayerOptions["linkTypes"]>[0] | null {
  return {
    name: "survey",
    label: "Survey",
    attrs: {
      href: "{{survey}}",
      target: "_blank",
    },
    fields: [
      {
        name: "survey",
        label: "Survey",
        inputType: null,
        options: (surveys ?? []).map((survey) => ({
          value: makeSurveyUrl(accountId, survey.id, { anonymous: false }),
          label: truncate(isEmpty(survey.title) ? "Untitled Survey" : survey.title, { length: 35 }),
        })),
        sortOptions: true,
      },
    ],
  };
}
